import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { CreditCard, Link2, RefreshCw, Copy, Banknote, ChevronDown, ChevronUp, Settings2, Save, CalendarClock, Zap, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mandateBadge = (status, hasMandate) => {
  if (hasMandate && status === 'active') return <Badge className="bg-green-100 text-green-700">Активен</Badge>;
  if (status === 'pending') return <Badge className="bg-amber-100 text-amber-700">Чака потвърждение</Badge>;
  if (status === 'failed') return <Badge className="bg-red-100 text-red-700">Неуспешен</Badge>;
  return <Badge className="bg-slate-100 text-slate-500">Ненастроен</Badge>;
};

const chargeBadge = (status) => {
  const map = {
    succeeded: ['bg-green-100 text-green-700', 'Успешно'],
    processing: ['bg-blue-100 text-blue-700', 'Обработва се'],
    failed: ['bg-red-100 text-red-700', 'Неуспешно'],
  };
  const [cls, label] = map[status] || ['bg-slate-100 text-slate-600', status || '-'];
  return <Badge className={cls}>{label}</Badge>;
};

export default function DirectDebitTab() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [defaultFee, setDefaultFee] = useState('');
  const [savingFee, setSavingFee] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linkDialog, setLinkDialog] = useState({ open: false, url: '', name: '' });
  const [feeDialog, setFeeDialog] = useState({ open: false, id: '', name: '', tuition_fee: '', discount: '' });
  const [amounts, setAmounts] = useState({});
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState('');
  const [auto, setAuto] = useState({ enabled: false, day: 1, reminder_days: 3 });
  const [savingAuto, setSavingAuto] = useState(false);
  const [runningBilling, setRunningBilling] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [ov, st, at] = await Promise.all([
        axios.get(`${API_URL}/online-payments/overview`, { headers }),
        axios.get(`${API_URL}/online-payments/settings`, { headers }),
        axios.get(`${API_URL}/online-payments/auto-settings`, { headers }),
      ]);
      setRows(ov.data);
      setDefaultFee(String(st.data.default_monthly_fee ?? ''));
      setAuto({ enabled: !!at.data.enabled, day: at.data.day || 1, reminder_days: at.data.reminder_days ?? 3 });
    } catch (e) {
      toast.error('Грешка при зареждане на плащанията');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveAuto = async (next) => {
    setSavingAuto(true);
    try {
      const res = await axios.put(`${API_URL}/online-payments/auto-settings`, next, { headers });
      setAuto({ enabled: !!res.data.enabled, day: res.data.day, reminder_days: res.data.reminder_days });
      toast.success('Настройките за автоматично таксуване са запазени');
    } catch (e) {
      toast.error('Грешка при запазване');
    } finally {
      setSavingAuto(false);
    }
  };

  const sendRemindersNow = async () => {
    if (!window.confirm('Да се изпратят напомнящи имейли на всички родители с активен мандат сега? (Вече изпратените този месец се прескачат.)')) return;
    setSendingReminders(true);
    try {
      const res = await axios.post(`${API_URL}/online-payments/send-reminders`, {}, { headers });
      const { sent = [], skipped = [] } = res.data;
      toast.success(`Изпратени напомняния: ${sent.length}, прескочени: ${skipped.length}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Грешка при изпращане на напомнянията');
    } finally {
      setSendingReminders(false);
    }
  };

  const runBillingNow = async () => {
    if (!window.confirm('Да се начисли месечната такса на всички ученици с активен мандат сега? (Вече начислените този месец се прескачат.)')) return;
    setRunningBilling(true);
    try {
      const res = await axios.post(`${API_URL}/online-payments/run-billing`, {}, { headers });
      const { charged = [], skipped = [], failed = [] } = res.data;
      toast.success(`Готово: начислени ${charged.length}, прескочени ${skipped.length}, неуспешни ${failed.length}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Грешка при таксуването');
    } finally {
      setRunningBilling(false);
    }
  };

  const saveDefaultFee = async () => {
    setSavingFee(true);
    try {
      await axios.put(`${API_URL}/online-payments/settings`,
        { default_monthly_fee: parseFloat(defaultFee) || 0 }, { headers });
      toast.success('Стандартната такса е запазена');
      fetchAll();
    } catch (e) {
      toast.error('Грешка при запазване');
    } finally {
      setSavingFee(false);
    }
  };

  const saveStudentFee = async () => {
    setBusy(feeDialog.id);
    try {
      await axios.put(`${API_URL}/online-payments/student/${feeDialog.id}/fee`, {
        tuition_fee: feeDialog.tuition_fee === '' ? null : parseFloat(feeDialog.tuition_fee),
        discount: parseFloat(feeDialog.discount) || 0,
      }, { headers });
      toast.success('Таксата е обновена');
      setFeeDialog({ open: false, id: '', name: '', tuition_fee: '', discount: '' });
      setAmounts({});
      fetchAll();
    } catch (e) {
      toast.error('Грешка при запазване на таксата');
    } finally {
      setBusy('');
    }
  };

  const generateLink = async (student) => {
    setBusy(student.student_id);
    try {
      const res = await axios.post(`${API_URL}/online-payments/mandate/setup`,
        { student_id: student.student_id, origin_url: window.location.origin }, { headers });
      setLinkDialog({ open: true, url: res.data.url, name: student.name });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Грешка при генериране на линк');
    } finally { setBusy(''); }
  };

  const syncMandate = async (student) => {
    setBusy(student.student_id);
    try {
      await axios.post(`${API_URL}/online-payments/mandate/sync?student_id=${student.student_id}`, {}, { headers });
      toast.success('Статусът е обновен');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Грешка при синхронизация');
    } finally { setBusy(''); }
  };

  const chargeStudent = async (student) => {
    const raw = amounts[student.student_id];
    const amount = parseFloat(raw !== undefined ? raw : student.net_fee);
    if (!amount || amount <= 0) { toast.error('Въведете валидна сума'); return; }
    setBusy(student.student_id);
    try {
      await axios.post(`${API_URL}/online-payments/charge`,
        { student_id: student.student_id, amount, description: 'Такса онлайн обучение' }, { headers });
      toast.success('Тегленето е стартирано');
      setAmounts((p) => ({ ...p, [student.student_id]: undefined }));
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Грешка при теглене');
    } finally { setBusy(''); }
  };

  const copyLink = () => { navigator.clipboard.writeText(linkDialog.url); toast.success('Линкът е копиран'); };

  return (
    <div className="space-y-4" data-testid="direct-debit-tab">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-indigo-600" /> Директен дебит (онлайн такси)
        </h2>
        <p className="text-sm text-slate-500">
          Изпратете линк на родителя да въведе банкови данни сигурно през Stripe, после начислете таксата.
        </p>
      </div>

      {/* Default monthly fee */}
      <Card className="p-4 flex flex-wrap items-end gap-3 bg-slate-50">
        <div>
          <Label htmlFor="default-fee">Стандартна месечна такса (£)</Label>
          <Input id="default-fee" data-testid="default-fee-input" type="number" min="0" step="0.01"
            value={defaultFee} onChange={(e) => setDefaultFee(e.target.value)} className="w-40" placeholder="напр. 60" />
        </div>
        <Button onClick={saveDefaultFee} disabled={savingFee} data-testid="save-default-fee-btn"
          className="bg-[#00966E] hover:bg-[#007a59] text-white">
          <Save className="h-4 w-4 mr-1" /> Запази
        </Button>
        <span className="text-xs text-slate-500 ml-auto self-center">Прилага се за деца без индивидуална такса. Отстъпката е фиксирана сума (£).</span>
      </Card>

      {/* Automated monthly billing */}
      <Card className="p-4 bg-indigo-50/50 border-indigo-100" data-testid="auto-charge-card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-indigo-600" />
            <div>
              <p className="font-semibold text-slate-900 text-sm">Автоматично месечно таксуване</p>
              <p className="text-xs text-slate-500">Всеки месец нето таксата се тегли автоматично от активните мандати.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Switch
              data-testid="auto-charge-switch"
              checked={auto.enabled}
              disabled={savingAuto}
              onCheckedChange={(v) => saveAuto({ enabled: v, day: auto.day, reminder_days: auto.reminder_days })}
            />
            <span className="text-sm text-slate-700">{auto.enabled ? 'Включено' : 'Изключено'}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mt-4 pt-3 border-t border-indigo-100">
          <div>
            <Label htmlFor="auto-day">Ден от месеца</Label>
            <Input
              id="auto-day" data-testid="auto-charge-day-input" type="number" min="1" max="28" step="1"
              value={auto.day}
              onChange={(e) => setAuto((p) => ({ ...p, day: e.target.value }))}
              onBlur={() => saveAuto({ enabled: auto.enabled, day: Math.min(28, Math.max(1, parseInt(auto.day, 10) || 1)), reminder_days: auto.reminder_days })}
              className="w-24"
            />
            <p className="text-xs text-slate-400 mt-1">1–28. Тегли се в 07:00 UTC.</p>
          </div>
          <div>
            <Label htmlFor="reminder-days">Напомняне (дни преди)</Label>
            <Input
              id="reminder-days" data-testid="reminder-days-input" type="number" min="0" max="14" step="1"
              value={auto.reminder_days}
              onChange={(e) => setAuto((p) => ({ ...p, reminder_days: e.target.value }))}
              onBlur={() => saveAuto({ enabled: auto.enabled, day: auto.day, reminder_days: Math.min(14, Math.max(0, parseInt(auto.reminder_days, 10) || 0)) })}
              className="w-28"
            />
            <p className="text-xs text-slate-400 mt-1">0 = без имейл напомняне.</p>
          </div>
          <Button
            onClick={runBillingNow} disabled={runningBilling} data-testid="run-billing-now-btn"
            variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-100">
            <Zap className="h-4 w-4 mr-1" /> {runningBilling ? 'Таксуване…' : 'Начисли всички сега'}
          </Button>
          <Button
            onClick={sendRemindersNow} disabled={sendingReminders} data-testid="send-reminders-now-btn"
            variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-100">
            <Mail className="h-4 w-4 mr-1" /> {sendingReminders ? 'Изпращане…' : 'Изпрати напомняния сега'}
          </Button>
        </div>
      </Card>

      {loading ? (
        <Card className="p-8 text-center text-slate-500">Зареждане...</Card>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">Няма онлайн ученици</Card>
      ) : (
        <div className="space-y-3">
          {rows.map((s) => (
            <Card key={s.student_id} data-testid="dd-student-row" className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{s.name}</span>
                    {mandateBadge(s.mandate_status, s.has_mandate)}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Родител: {s.parent_email || '—'}</p>
                  <p className="text-xs text-slate-600 mt-1">
                    Такса: <strong>£{Number(s.effective_fee).toFixed(2)}</strong>
                    {s.tuition_fee != null && <span className="text-indigo-600"> (индивидуална)</span>}
                    {s.discount > 0 && <span> · Отстъпка: −£{Number(s.discount).toFixed(2)}</span>}
                    {' · '}Нето: <strong className="text-[#00966E]">£{Number(s.net_fee).toFixed(2)}</strong>
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="ghost" data-testid="dd-fee-btn"
                    onClick={() => setFeeDialog({ open: true, id: s.student_id, name: s.name,
                      tuition_fee: s.tuition_fee != null ? String(s.tuition_fee) : '',
                      discount: s.discount ? String(s.discount) : '' })}
                    title="Настрой такса и отстъпка">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === s.student_id}
                    onClick={() => generateLink(s)} data-testid="dd-link-btn">
                    <Link2 className="h-4 w-4 mr-1" /> Линк за дебит
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy === s.student_id}
                    onClick={() => syncMandate(s)} data-testid="dd-sync-btn" title="Обнови статус на мандата">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-1">
                    <Input type="number" min="0" step="0.01" placeholder="£ сума"
                      value={amounts[s.student_id] !== undefined ? amounts[s.student_id] : (s.net_fee || '')}
                      onChange={(e) => setAmounts((p) => ({ ...p, [s.student_id]: e.target.value }))}
                      className="w-24 h-9" data-testid="dd-amount-input" />
                    <Button size="sm" disabled={!s.has_mandate || busy === s.student_id}
                      onClick={() => chargeStudent(s)} data-testid="dd-charge-btn"
                      className="bg-[#00966E] hover:bg-[#007a59] text-white">
                      <Banknote className="h-4 w-4 mr-1" /> Начисли
                    </Button>
                  </div>
                  {s.charges.length > 0 && (
                    <Button size="sm" variant="ghost"
                      onClick={() => setExpanded((p) => ({ ...p, [s.student_id]: !p[s.student_id] }))}
                      data-testid="dd-history-toggle">
                      История ({s.charges.length})
                      {expanded[s.student_id] ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                    </Button>
                  )}
                </div>
              </div>

              {expanded[s.student_id] && s.charges.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-1">
                  {s.charges.map((c) => (
                    <div key={c.charge_id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">
                        {new Date(c.created_at).toLocaleDateString('bg-BG')} · £{Number(c.amount).toFixed(2)} · {c.description}
                      </span>
                      {chargeBadge(c.status)}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Per-student fee dialog */}
      <Dialog open={feeDialog.open} onOpenChange={(o) => setFeeDialog((p) => ({ ...p, open: o }))}>
        <DialogContent data-testid="dd-fee-dialog">
          <DialogHeader><DialogTitle>Такса и отстъпка — {feeDialog.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="fee-tuition">Индивидуална месечна такса (£)</Label>
              <Input id="fee-tuition" data-testid="fee-tuition-input" type="number" min="0" step="0.01"
                value={feeDialog.tuition_fee}
                onChange={(e) => setFeeDialog((p) => ({ ...p, tuition_fee: e.target.value }))}
                placeholder="празно = стандартна такса" />
              <p className="text-xs text-slate-400 mt-1">Остави празно, за да ползва стандартната такса.</p>
            </div>
            <div>
              <Label htmlFor="fee-discount">Отстъпка (£)</Label>
              <Input id="fee-discount" data-testid="fee-discount-input" type="number" min="0" step="0.01"
                value={feeDialog.discount}
                onChange={(e) => setFeeDialog((p) => ({ ...p, discount: e.target.value }))}
                placeholder="0" />
            </div>
            <Button onClick={saveStudentFee} disabled={busy === feeDialog.id} data-testid="fee-save-btn"
              className="w-full bg-[#00966E] hover:bg-[#007a59] text-white">Запази</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mandate link dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(o) => setLinkDialog((p) => ({ ...p, open: o }))}>
        <DialogContent data-testid="dd-link-dialog">
          <DialogHeader><DialogTitle>Линк за директен дебит — {linkDialog.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Изпратете този защитен линк на родителя. Той въвежда банковите данни директно при Stripe и потвърждава мандата.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Input readOnly value={linkDialog.url} className="text-xs" data-testid="dd-link-url" />
            <Button size="sm" onClick={copyLink} data-testid="dd-copy-btn"><Copy className="h-4 w-4 mr-1" /> Копирай</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
